import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { getApiErrorMessage } from '../../lib/apiErrors';
import { BILLING_PLANS, type BillingPlan } from '../../lib/billingCatalog';

interface PromoCode {
  id: string;
  code: string;
  percentOff: number | null;
  amountOffCents: number | null;
  currency: string | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  expiresAt: string | null;
  appliesToPlans: string[];
  active: boolean;
  createdAt: string;
}

export function AdminPromoCodesPage() {
  const [rows, setRows] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [percentOff, setPercentOff] = useState('');
  const [amountOffCents, setAmountOffCents] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [appliesToPlans, setAppliesToPlans] = useState<BillingPlan[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ promoCodes: PromoCode[] }>('/admin/promo-codes');
      setRows(res.data.promoCodes);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        code: code.trim(),
        appliesToPlans,
      };
      if (percentOff.trim()) payload.percentOff = Number(percentOff);
      if (amountOffCents.trim()) payload.amountOffCents = Number(amountOffCents);
      if (currency.trim()) payload.currency = currency.trim();
      if (maxRedemptions.trim()) payload.maxRedemptions = Number(maxRedemptions);
      if (expiresAt.trim()) payload.expiresAt = new Date(expiresAt).toISOString();
      await api.post('/admin/promo-codes', payload);
      setCode('');
      setPercentOff('');
      setAmountOffCents('');
      setMaxRedemptions('');
      setExpiresAt('');
      setAppliesToPlans([]);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Failed to create promo code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await api.patch(`/admin/promo-codes/${id}/deactivate`);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Failed to deactivate');
    }
  };

  const togglePlan = (plan: BillingPlan) => {
    setAppliesToPlans((cur) =>
      cur.includes(plan) ? cur.filter((p) => p !== plan) : [...cur, plan],
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Promo codes</h1>
        <p className="text-sm text-gray-600 mt-1">
          Create Stripe-backed promotion codes customers can apply at checkout.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="border border-gray-200 rounded-lg p-4 bg-white space-y-3"
        aria-label="Create promo code"
      >
        <h2 className="text-lg font-semibold text-gray-900">New code</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700">Code</span>
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
              placeholder="LAUNCH20"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Percent off (0–100)</span>
            <input
              type="number"
              value={percentOff}
              onChange={(e) => setPercentOff(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Amount off (cents)</span>
            <input
              type="number"
              value={amountOffCents}
              onChange={(e) => setAmountOffCents(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Currency (for amount off)</span>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toLowerCase())}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Max redemptions</span>
            <input
              type="number"
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Expires at</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            />
          </label>
        </div>
        <fieldset>
          <legend className="text-sm text-gray-700">Applies to plans</legend>
          <div className="flex gap-2 mt-1">
            {BILLING_PLANS.map((p) => (
              <label key={p} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={appliesToPlans.includes(p)}
                  onChange={() => togglePlan(p)}
                />
                {p}
              </label>
            ))}
          </div>
        </fieldset>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded hover:bg-green-800 disabled:opacity-60"
        >
          {submitting ? 'Creating…' : 'Create code'}
        </button>
      </form>

      <section className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <h2 className="text-lg font-semibold text-gray-900 p-4 border-b border-gray-100">
          Existing codes
        </h2>
        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No promo codes yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                <th className="p-2">Code</th>
                <th className="p-2">Discount</th>
                <th className="p-2">Plans</th>
                <th className="p-2">Redemptions</th>
                <th className="p-2">Expires</th>
                <th className="p-2">Status</th>
                <th className="p-2" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="p-2 font-mono">{r.code}</td>
                  <td className="p-2">
                    {r.percentOff != null
                      ? `${r.percentOff}% off`
                      : r.amountOffCents != null
                        ? `${(r.amountOffCents / 100).toFixed(2)} ${r.currency?.toUpperCase() ?? ''} off`
                        : '—'}
                  </td>
                  <td className="p-2">{r.appliesToPlans.join(', ') || 'any'}</td>
                  <td className="p-2">
                    {r.timesRedeemed}
                    {r.maxRedemptions != null ? ` / ${r.maxRedemptions}` : ''}
                  </td>
                  <td className="p-2">
                    {r.expiresAt ? new Date(r.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-2">
                    {r.active ? (
                      <span className="text-green-700">active</span>
                    ) : (
                      <span className="text-gray-500">inactive</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {r.active && (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(r.id)}
                        className="text-sm text-red-700 hover:underline"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export default AdminPromoCodesPage;
